/**
 * LiquidGlassSVG — the shell-level, hidden <defs> host for STATIC named Liquid Glass
 * filters that CSS references by id (currently: `#lg-lens`, MERIDIAN's reserved
 * "Lens" moment). Mounted ONCE in AppShell, alongside the MapBackdrop it serves.
 *
 * Why a static, size-independent filter here (vs. the per-element, dynamically-sized
 * maps the useLiquidGlass hook builds):
 *   • The Lens acts on the full-viewport frozen Terrain — far past the ~800px
 *     displacement ceiling for a fitted map — so it cannot use a fitted map. A
 *     `feImage` displacement map with `preserveAspectRatio="none"` stretches to the
 *     filter region at any size; the DIRECTION of displacement is what a lens needs,
 *     and the MAGNITUDE is the `feDisplacementMap` scale — both size-independent.
 *   • It rides `filter:` on an ordinary element (the Terrain image), NOT
 *     `backdrop-filter:` — so it renders in Chromium, Safari AND Firefox. The Lens
 *     is therefore the one refraction that is genuinely cross-browser.
 *
 * The displacement map is the same construction the hook uses (a horizontal red /
 * vertical blue gradient pair + a blurred neutral-gray interior), so the whole app
 * speaks one refraction vocabulary. `color-interpolation-filters="sRGB"` is
 * MANDATORY (default linearRGB re-maps the neutral gray → phantom whole-image
 * displacement — adoption doc gotcha #1).
 *
 * Purely decorative: aria-hidden, zero-size, pointer-events:none, focus-inert. The
 * CSS that references `#lg-lens` gates it behind prefers-reduced-motion, and the
 * Terrain is opacity:0 under reduced-transparency, so the Lens self-neutralises
 * under every accessibility preference with no logic here.
 */

/** The static "Lens" displacement filter id, referenced from MapBackdrop.css. */
export const LG_LENS_FILTER_ID = 'lg-lens';

/**
 * A size-independent barrel-lens displacement map: red ramps left→right (X
 * displacement), blue ramps top→bottom (Y displacement), and a blurred neutral-gray
 * interior confines the bend to the edges — so a full-plane image reads as gently
 * bowed toward the viewer, "a lens pressed over the world." Encoded once as a data
 * URI at a fixed viewBox; `preserveAspectRatio="none"` stretches it to the viewport.
 */
const LENS_MAP_URI =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg">' +
      '<defs>' +
      '<linearGradient id="r" x1="100%" y1="0%" x2="0%" y2="0%">' +
      '<stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="red"/></linearGradient>' +
      '<linearGradient id="b" x1="0%" y1="0%" x2="0%" y2="100%">' +
      '<stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="blue"/></linearGradient>' +
      '</defs>' +
      '<rect width="160" height="160" fill="black"/>' +
      '<rect width="160" height="160" rx="28" fill="url(#r)"/>' +
      '<rect width="160" height="160" rx="28" fill="url(#b)" style="mix-blend-mode:difference"/>' +
      // Interior = FULLY OPAQUE neutral gray (R=B=0.5 → zero displacement). A partial
      // alpha would let the edge gradients bleed through and bow the plane's center;
      // Liquid Glass bends only the edges. Keep alpha at 1.
      '<rect x="18" y="18" width="124" height="124" rx="28" fill="hsl(0 0% 50%)" style="filter:blur(12px)"/>' +
      '</svg>',
  );

export const LiquidGlassSVG = () => (
  <svg
    className="lg-svg-defs"
    width="0"
    height="0"
    aria-hidden="true"
    focusable="false"
    style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }}
  >
    <defs>
      {/* THE LENS — a subtle barrel bow of the frozen Terrain, held while a major
          overlay is open (reserved MERIDIAN moment). Static: no idle animation. */}
      <filter
        id={LG_LENS_FILTER_ID}
        x="-6%"
        y="-6%"
        width="112%"
        height="112%"
        colorInterpolationFilters="sRGB"
      >
        <feImage href={LENS_MAP_URI} result="lensMap" preserveAspectRatio="none" />
        <feDisplacementMap
          in="SourceGraphic"
          in2="lensMap"
          scale="16"
          xChannelSelector="R"
          yChannelSelector="B"
        />
      </filter>
    </defs>
  </svg>
);
